// @flow
import { isAfter, isBefore, isPast, parse, parseISO } from "date-fns";
import Config from "../constants/Config";
import Store from "../store";
import RenderJsxHtml from "./RenderJsxHtml";
import { getDateString, getMembershipDateString } from "./date";
import { maybePluralize } from "./stringHandler";
import { parseDateString } from "../utils/date";
import getProductPriceHtml from "./getProductPriceHtml";
// import formatCurrency from "./formatCurrency";

const hostedByList = (hosted_by_activateuts, hosted_by) => {
    const hosts = [];
    if (hosted_by_activateuts) {
        hosts.push({
            tag: "ActivateUTS",
        });
    }

    hosted_by.forEach(host => {
        hosts.push({
            url: host.club_link,
            tag: host.club_name,
        });
    });

    return hosts;
};

const getContentReferenceTileProps = (postData: any, context?: string): any => {
    const options = Store.getState().options;
    const user = Store.getState().user;
    const loggedIn = user.loggedIn;
    const userType = user.profile ? user.profile.type : "";

    const now = new Date();

    const postType =
        "purchasable" in postData
            ? "product" // enforce "product" post type for products
            : postData.post_type || postData.type;
    const eventStartDate =
        postData.acf && postData.acf.start_date
            ? getDateString(
                  postData.acf.start_date,
                  postType === "club_event" || context === "club-event-detail"
              )
            : "";
    const eventEndDate =
        postData.acf && postData.acf.end_date
            ? getDateString(
                  postData.acf.end_date,
                  postType === "club_event" || context === "club-event-detail"
              )
            : "";
    const isPastEvent =
        postData.acf &&
        postData.acf.end_date &&
        isPast(parse(postData.acf.end_date, "dd/MM/yyyy h:mm a", new Date()));

    const postTypeDefaults = {
        // PAGE
        page: {
            eyebrow: postData.acf.tile_eyebrow,
            url: postData.url,
            featured: postData.acf.featured,
            title: postData.title,
            description: postData.acf.excerpt,
            image: postData.acf.tile_image
                ? postData.acf.tile_image
                : options.default_tile_image,
        },
        // PRODUCT
        product: {
            onSale: postData.on_sale,
            badge: Number(postData.price) ? (
                <RenderJsxHtml>
                    {getProductPriceHtml({
                        type: postData.type,
                        price: postData.price,
                        regularPrice: postData.regular_price,
                        salePrice: postData.sale_price,
                        variationPricesMin: postData.variation_prices?.min,
                        variationPricesMax: postData.variation_prices?.max,
                        userDiscountPercentage:
                            user.nonce?.user_discount_percentage ?? 0,
                        onSale: postData.on_sale,
                        productDiscountable: postData.uts_discountable,
                    })}
                </RenderJsxHtml>
            ) : (
                "Free"
            ),
            eyebrow: loggedIn
                ? // logged in
                  postData.on_sale
                    ? "Sale"
                    : postData.uts_discountable &&
                      Config.discountUserTypes.includes(userType)
                    ? "UTS 10% discount applied"
                    : ""
                : // logged out -> check if on sale -> check if discountable
                postData.on_sale
                ? "Sale"
                : postData.uts_discountable
                ? "10% off for UTS students and staff"
                : "",
            url: postData.permalink || postData.url,
            featured: postData.featured,
            title: postData.name,
            description: postData.short_description,
            tags:
                postData.attributes && postData.attributes.length
                    ? postData.attributes.length === 1 &&
                      postData.attributes[0].name === "Clothing Size"
                        ? null
                        : ["More options available"]
                    : null,
            image:
                postData.images && postData.images.length > 0
                    ? postData.images[0]
                    : options.default_tile_image,
            alert:
                postData.stock_status !== "instock"
                    ? "Out of stock"
                    : postData.stock_quantity &&
                      postData.low_stock_amount &&
                      Number(postData.stock_quantity) <=
                          Number(postData.low_stock_amount)
                    ? "Low stock"
                    : null,
        },
        // AD
        ad: {
            badge: "AD",
            url: postData.acf.link ? postData.acf.link.url : "",
            target: postData.acf.link ? postData.acf.link.target : "",
            title: postData.title,
            image: postData.acf.image,
        },
        // CLUB
        club: {
            logo: postData.acf.logo ? postData.acf.logo.url : null,
            url: postData.url,
            featured: postData.acf.featured,
            title: postData.title,
            description: postData.acf.description,
            tags: postData.upcoming_events_count
                ? [
                      `${maybePluralize(
                          postData.upcoming_events_count,
                          "upcoming event"
                      )}`,
                  ]
                : null,
            image:
                postData.acf.media &&
                postData.acf.media[0] &&
                postData.acf.media[0].image
                    ? postData.acf.media[0].image
                    : options.default_tile_image,
        },
        // CLUB
        club_membership: {
            logo: postData.club_logo,
            url: postData.club_url,
            title: postData.club_name,
            image: postData.club_media,
            joined_date: getMembershipDateString(postData.joined_date),
            expiry_date: getMembershipDateString(postData.expiry_date),
            expanded: true,
        },
        // EVENT
        event: {
            badge:
                eventStartDate === eventEndDate
                    ? eventStartDate
                    : `${eventStartDate} - ${eventEndDate}`,
            isPastEvent,
            eyebrow: postData.acf.date_description,
            url: postData.url,
            featured:
                (postData.acf.featured && context !== "club-detail") ||
                (postData.acf.club_featured && context === "club-detail"),
            title: postData.post_title || postData.title,
            description: postData.acf.hero
                ? postData.acf.hero.description
                : null,
            tags:
                postData.acf && postData.acf.hosted_by
                    ? postData.acf.hosted_by.map(host => {
                          return {
                              url: "/clubs/" + host.post_name,
                              tag: host.post_title,
                          };
                      })
                    : [],
            image:
                postData.acf &&
                postData.acf.hero &&
                postData.acf.hero.media &&
                postData.acf.hero.media[0].image
                    ? postData.acf.hero.media[0].image
                    : options.default_tile_image,
            sold_out: postData.acf.sold_out,
            cancelled: postData.acf.open_state === "cancelled",
        },
        // CLUB EXEC : EVENT
        club_event: {
            badge:
                eventStartDate === eventEndDate
                    ? eventStartDate
                    : `${eventStartDate} - ${eventEndDate}`,
            url: postData.url,
            title: postData.post_title || postData.title,
            description: `${postData.event_ticket_sold || 0} Tickets sold`,
            status: postData.post_status || postData.status,
            image:
                postData.acf && postData.acf.hero && postData.acf.hero.media
                    ? postData.acf.hero.media[0].image
                    : options.default_tile_image,
            expanded: true,
            manualAttendanceAllowed:
                !postData?.event_checkin_count &&
                !postData?.event_attendance?.is_submitted &&
                (!postData?.acf?.end_date ||
                    isAfter(now, parseDateString(postData?.acf?.end_date))) &&
                (!postData?.event_attendance?.submission_until ||
                    isBefore(
                        now,
                        parseISO(postData?.event_attendance?.submission_until)
                    )),
        },
        // TICKET
        ticket: {
            badge:
                postData.event_start_date != null
                    ? getDateString(
                          postData.event_start_date,
                          true,
                          "yyyy-MM-dd HH:mm:ss"
                      ) ===
                      (postData.event_end_date &&
                          getDateString(
                              postData.event_end_date,
                              true,
                              "yyyy-MM-dd HH:mm:ss"
                          ))
                        ? getDateString(
                              postData.event_start_date,
                              true,
                              "yyyy-MM-dd HH:mm:ss"
                          )
                        : `${getDateString(
                              postData.event_start_date,
                              true,
                              "yyyy-MM-dd HH:mm:ss"
                          )} - ${getDateString(
                              postData.event_end_date,
                              true,
                              "yyyy-MM-dd HH:mm:ss"
                          )}`
                    : null,
            url: postData.event_link,
            title: postData.event_name,
            tags:
                postType === "ticket"
                    ? hostedByList(
                          postData.hosted_by_activateuts,
                          postData.hosted_by
                      )
                    : [],
            image: postData.event_image,
            ticket_count: postData.tickets ? postData.tickets.length : 0,
            expanded: true,
        },
    };

    return {
        ...postTypeDefaults[postType],
        variation: postType,
    };
};

export default getContentReferenceTileProps;
